import { RecentlyViewed } from '@theme/recently-viewed-products';

// Custom add-on: Horizon remains the sole writer/owner of the browsing history.
function previousProductIds(currentId) {
  try {
    const stored = RecentlyViewed.getProducts();
    if (!Array.isArray(stored)) return [];
    return [...new Set(stored.map(String))]
      .filter((id) => /^\d+$/.test(id) && id !== currentId)
      .slice(0, 3);
  } catch {
    // Storage may be blocked or contain invalid JSON. Keep the row hidden.
    return [];
  }
}

class JowissaRecentlyViewed extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-jrv-list]');
    if (!this.list) return;
    this.refresh = this.refresh.bind(this);
    this.onPageShow = (event) => { if (event.persisted) this.refresh(); };
    this.onStorage = (event) => {
      if (event.key === 'viewedProducts' || event.key === null) this.refresh();
    };
    this.onClear = (event) => {
      // Honour Horizon's existing Clear action in its search panel, too.
      if (event.target instanceof Element &&
          event.target.closest('[on\\:click="/clearRecentlyViewedProducts"]')) {
        queueMicrotask(this.refresh);
      }
    };
    window.addEventListener('pageshow', this.onPageShow);
    window.addEventListener('storage', this.onStorage);
    document.addEventListener('click', this.onClear);
    this.refresh();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.controller?.abort();
    this.controller = null;
    window.removeEventListener('pageshow', this.onPageShow);
    window.removeEventListener('storage', this.onStorage);
    document.removeEventListener('click', this.onClear);
  }

  refresh() {
    if (!this.isConnected) return;
    this.observer?.disconnect();
    this.controller?.abort();
    this.controller = null;
    this.ids = previousProductIds(this.dataset.currentProduct);
    this.list.replaceChildren();
    this.hidden = this.ids.length === 0 && !this.hasAttribute('data-design-mode');
    this.removeAttribute('aria-busy');
    if (!this.ids.length) return;

    this.setAttribute('aria-busy', 'true');
    for (const id of this.ids) {
      const item = document.createElement('li');
      item.className = 'jrv__item jrv__skeleton';
      item.setAttribute('aria-hidden', 'true');
      this.list.append(item);
    }

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.loadCards();
      }, { rootMargin: '600px 0px' });
      // The host is visible with placeholders, so it can be observed reliably.
      this.observer.observe(this);
    } else {
      this.loadCards();
    }
  }

  async loadCards() {
    const controller = new AbortController();
    this.controller = controller;
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const url = new URL(this.dataset.searchUrl, window.location.origin);
      if (url.origin !== window.location.origin) throw new Error('Unexpected search origin');
      url.searchParams.set('q', this.ids.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'none');
      url.searchParams.set('section_id', 'predictive-search');
      // Keep an unpublished-theme preview on that theme, where explicitly present.
      const previewId = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (previewId) url.searchParams.set('preview_theme_id', previewId);

      const response = await fetch(url, {
        credentials: 'same-origin',
        signal: controller.signal,
      });
      if (!response.ok) throw new Error('Recently viewed request failed');
      // Template contents stay inert: discard hover images before inserting cards.
      const template = document.createElement('template');
      template.innerHTML = await response.text();
      const markup = template.content;
      if (controller.signal.aborted || !this.isConnected) return;

      const items = [];
      // Horizon returns these in browsing-history order, not search-ranking order.
      for (const card of markup.querySelectorAll('.recently-viewed-wrapper .resource-card__wrapper')) {
        const link = card.querySelector('.resource-card__link');
        const href = link?.getAttribute('href');
        if (!href) continue; // Removed/unpublished products can produce empty cards.
        const destination = new URL(href, url);
        if (destination.origin !== url.origin || !destination.pathname.includes('/products/')) continue;
        for (const key of ['_pos', '_sid', '_ss', '_fid']) destination.searchParams.delete(key);
        link.setAttribute('href', destination.pathname + destination.search + destination.hash);

        // One image per card: do not fetch hover images for this compact row.
        card.querySelectorAll('.resource-card__image--secondary').forEach((image) => image.remove());
        const image = card.querySelector('.resource-card__image');
        if (image) {
          image.setAttribute('sizes', '(max-width: 749px) 44vw, 280px');
          image.setAttribute('loading', 'lazy');
          image.setAttribute('decoding', 'async');
        }
        // Do not label this custom row as a search-result impression.
        card.removeAttribute('view-event-payload');
        const item = document.createElement('li');
        item.className = 'jrv__item';
        item.append(document.importNode(card, true));
        items.push(item);
      }

      this.list.replaceChildren(...items.slice(0, 3));
      this.hidden = items.length === 0 && !this.hasAttribute('data-design-mode');
    } catch {
      // Fail quietly. Never leave a broken or permanently loading shopping row.
      if (this.controller === controller) {
        this.list.replaceChildren();
        this.hidden = !this.hasAttribute('data-design-mode');
      }
    } finally {
      clearTimeout(timeout);
      if (this.controller === controller) this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('jowissa-recently-viewed')) {
  customElements.define('jowissa-recently-viewed', JowissaRecentlyViewed);
}